import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link2, Unlink, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function StravaConnectCard() {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/strava/status`);
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // If we just came back from callback, show toast
    const params = new URLSearchParams(window.location.search);
    if (params.get('strava') === 'connected') {
      toast.success('¡Strava conectado! Sincronizando tus últimas actividades…');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('strava') === 'error') {
      const reason = params.get('reason') || 'desconocido';
      toast.error(`Error conectando Strava: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      const r = await axios.get(`${API}/strava/connect`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error('No se pudo iniciar la conexión con Strava');
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar Strava? Se mantendrán las actividades ya sincronizadas.')) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/strava/disconnect`);
      toast.success('Strava desconectado');
      setStatus({ connected: false });
    } catch (e) {
      toast.error('Error al desconectar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async () => {
    setActionLoading(true);
    try {
      const r = await axios.post(`${API}/strava/sync?dias=30`);
      const n = r.data.synced || 0;
      toast.success(n > 0 ? `Sincronizadas ${n} actividades nuevas` : 'No hay nuevas actividades para sincronizar');
    } catch (e) {
      toast.error('Error sincronizando');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="card-mk border-orange-700/40 mt-6 bg-gradient-to-br from-gray-900 to-orange-950/10" data-testid="strava-connect-card">
      <CardHeader>
        <CardTitle className="text-orange-400 flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current"><path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/></svg>
          Strava
        </CardTitle>
        <CardDescription className="text-gray-400">
          Sincroniza automáticamente tus carreras, bici y caminatas con tu Calendario de Preparación.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status.connected ? (
          <>
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle2 className="h-4 w-4" />
              <span>Conectado como <strong>{status.athlete_name || 'atleta'}</strong></span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSync}
                disabled={actionLoading}
                variant="outline"
                className="border-orange-600 text-orange-400 hover:bg-orange-950/30"
                data-testid="btn-strava-sync"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${actionLoading ? 'animate-spin' : ''}`} />
                Sincronizar últimos 30 días
              </Button>
              <Button
                onClick={handleDisconnect}
                disabled={actionLoading}
                variant="outline"
                className="border-gray-600 text-gray-300"
                data-testid="btn-strava-disconnect"
              >
                <Unlink className="h-4 w-4 mr-2" />
                Desconectar
              </Button>
            </div>
          </>
        ) : (
          <Button
            onClick={handleConnect}
            disabled={actionLoading}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-strava-connect"
          >
            <Link2 className="h-4 w-4 mr-2" />
            Conectar con Strava
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
